import IconLowWifi from "@/assets/teacher-class/slow-wifi.svg";
import { StudentState } from "@/store/room/interface";
import { calColorByBackground, generateAvatar } from "@/utils/utils";
import { useElementBounding } from "@vueuse/core";
import "animate.css";
import { computed, defineComponent, ref } from "vue";
import { useStore } from "vuex";
import StudentBadge from "../student-badge/student-badge.vue";
import { StudentCardActions } from "../student-card-actions";
import IndependentProgress from "./components/independent-progress/independent-progress.vue";
import { WifiIcon } from "@heroicons/vue/20/solid";
import { useManipulateTeamState } from "@/hooks/use-team";
import { TeamState } from "@/store/teams/state";
import { isUserPublishedAudio, isUserPublishedVideo } from "@/store/calling/utils";
import { MODULES, vuexName } from "@/store/utils";
import { RoomStore, useRoomStore } from "@/hooks/use-room-store";
import { LowBandWidthLocales } from "@/locales/localeid";
import { fmtMsg } from "vue-glcommonui";

export default defineComponent({
  methods: { calColorByBackground },
  components: {
    WifiIcon,
    StudentBadge,
    StudentCardActions,
    IndependentProgress,
  },
  props: {
    setModeOne: {
      type: Boolean,
      default: false,
    },
    student: { type: Object as () => StudentState, required: true },
    isLarge: Boolean,
    focusStudentId: String,
    scaleOption: Number,
  },

  setup(props) {
    const { getters, dispatch, commit } = useStore();
    const isHelper = computed(() => getters[vuexName(MODULES.TEACHER.GETTERS.GET_IS_HELPER)]);
    const poorConnectionText = computed(() => fmtMsg(LowBandWidthLocales.VideoLowBandWidthMessage));
    const agoraVideoContainerRef = ref<HTMLDivElement | null>(null);
    const isMouseEntered = ref<boolean>(false);
    const isOneToOneStudent = computed(() => {
      return getters[vuexName(MODULES.TEACHER_ROOM.GETTERS.GET_STUDENT_MODE_ONE_ID)] === props.student.id;
    });
    const focusedStudent = computed(() => props.focusStudentId === props.student.id);
    const { findTeamByStudentId, isTeamMode } = useManipulateTeamState();
    const studentTeam = computed<TeamState | undefined>(() => (isTeamMode.value ? findTeamByStudentId(props.student.id) : undefined));
    const hiddenByOneOneMode = computed(
      () =>
        getters[vuexName(MODULES.TEACHER_ROOM.GETTERS.GET_STUDENT_MODE_ONE_ID)] &&
        getters[vuexName(MODULES.TEACHER_ROOM.GETTERS.GET_STUDENT_MODE_ONE_ID)] !== props.student.id,
    );
    const currentExposure = computed(() => getters[vuexName(MODULES.LESSON.GETTERS.GET_CURRENT_EXPOSURE)]);
    const currentExposureItemMedia = computed(() => getters[vuexName(MODULES.LESSON.GETTERS.GET_CURRENT_EXPOSURE_ITEM_MEDIA)]);
    const isLowBandWidth = computed(() => {
      const listStudentLowBandWidth = getters[vuexName(MODULES.TEACHER_ROOM.GETTERS.GET_LIST_STUDENT_LOW_BAND_WIDTH)];
      return listStudentLowBandWidth.findIndex((id: string) => id === props.student.id) > -1;
    });
    const { oneStudentIds } = useRoomStore(RoomStore.Teacher);
    const oneAndOne = computed(() => getters[vuexName(MODULES.TEACHER_ROOM.GETTERS.GET_STUDENT_MODE_ONE_ID)]);
    const isAudioPublished = computed<boolean>(() => isUserPublishedAudio({ userId: props.student.id, gettersOrRootGetters: getters }));
    const isVideoPublished = computed<boolean>(() => isUserPublishedVideo({ userId: props.student.id, gettersOrRootGetters: getters }));
    const enterOneToOneWithStudent = async () => {
      if (oneStudentIds.value.includes(props.student?.id)) return;
      if (props.setModeOne && props.student?.id !== oneAndOne.value) {
        if (currentExposure.value?.id) commit(vuexName(MODULES.LESSON.COMMITS.SET_PREVIOUS_EXPOSURE), { id: currentExposure.value.id });
        if (currentExposureItemMedia.value)
          commit(vuexName(MODULES.LESSON.COMMITS.SET_PREVIOUS_EXPOSURE_ITEM_MEDIA), { id: currentExposureItemMedia.value.id });
        await dispatch(vuexName(MODULES.TEACHER_ROOM.DISPATCHES.SEND_ONE_AND_ONE), {
          enabled: true,
          id: props.student.id,
          isHelper: isHelper.value,
        });
      }
    };

    const avatarUrl = computed(() => generateAvatar(getters[vuexName(MODULES.TEAMS.GETTERS.GET_AVATAR_URL_BY_ID)](props.student.id)));

    const onDragStart = (event: any) => {
      event.dataTransfer.setData("studentId", props.student.id);
    };

    const onMouseChange = (entered: boolean) => {
      if (isStudentOneToOneWithAnotherTeacher.value) {
        return (isMouseEntered.value = false);
      }
      isMouseEntered.value = entered;
    };

    const isSpeaking = computed(() => {
      const speakingUsers: Array<string> = getters[vuexName(MODULES.TEACHER_ROOM.GETTERS.GET_SPEAKING_USERS)];
      return speakingUsers.indexOf(props.student.id) >= 0;
    });

    const studentRef = ref<any>(null);
    const parentCard = computed(() => studentRef.value?.parentElement);
    const { width } = useElementBounding(studentRef);
    const { width: parentWidth } = useElementBounding(parentCard);

    const maxScaleRatio = computed(() => {
      return width.value ? parentWidth.value / width.value : 1;
    });
    const actualScaleRatio = computed(() => {
      return Math.min(props.scaleOption || 1, maxScaleRatio.value);
    });
    const isTurnOnCamera = computed(() => {
      return isVideoPublished.value;
    });
    const isIndependentMode = computed<boolean>(() => getters[vuexName(MODULES.CLASS_TEACHING.GETTERS.GET_IS_INDEPENDENT_MODE)]);
    const isStudentOneToOneWithAnotherTeacher = computed(
      () => getters[vuexName(MODULES.TEACHER_ROOM.GETTERS.GET_STUDENT_MODE_ONE_WITH_ANOTHER_TEACHER_ID)] === props.student.id,
    );

    const borderLayers = computed(() => {
      const layers = [];
      if (props.student.raisingHand) {
        layers.push("border-red-600 animate-pulse");
      }
      if (props.student.isPalette) {
        layers.push("border-slate-900");
      }
      if (isSpeaking.value) {
        layers.push("border-blue-600");
      }
      return layers;
    });

    return {
      onDragStart,
      enterOneToOneWithStudent,
      onMouseChange,
      isMouseEntered,
      isSpeaking,
      hiddenByOneOneMode,
      IconLowWifi,
      isLowBandWidth,
      focusedStudent,
      studentRef,
      isTurnOnCamera,
      isOneToOneStudent,
      avatarUrl,
      oneAndOne,
      actualScaleRatio,
      maxScaleRatio,
      agoraVideoContainerRef,
      isIndependentMode,
      isStudentOneToOneWithAnotherTeacher,
      borderLayers,
      studentTeam,
      isAudioPublished,
      poorConnectionText,
    };
  },
});
